import {Component, Input, numberAttribute, OnInit} from '@angular/core';
import {AuthService} from '../../services/auth.service';
import {BehaviorSubject, distinctUntilChanged, map, switchMap, takeWhile, timer} from 'rxjs';
import {Router, RouterModule} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {CodeInputModule} from "@fastenhealth/angular-code-input";
import {CommonModule} from "@angular/common";
import {ApiMode, ConfigService} from "@shared-library";
import {DevToolsComponent} from "../../components/dev-tools/dev-tools.component";
import {FdkButtonComponent, FdkAlertComponent} from "fasten-design-kit";

@Component({
    selector: 'app-vault-profile-signin-code',
    templateUrl: './vault-profile-signin-code.component.html',
    styleUrls: ['./vault-profile-signin-code.component.css'],
    standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CodeInputModule,
    DevToolsComponent,
    FdkButtonComponent,
    FdkAlertComponent,
  ]
})
export class VaultProfileSigninCodeComponent implements OnInit {
  loading: boolean = false
  resendLoading: boolean = false
  errorMsg: string = ""
  canSkipSignInCode: boolean = false

  @Input() currentEmail = "test@example.com"
  private readonly codeExpiresAt$ = new BehaviorSubject<number>(0)

  @Input({transform: numberAttribute})
  set codeExpiresAt(value: number) {
    this.codeExpiresAt$.next(value)
  }

  get codeExpiresAt(): number {
    return this.codeExpiresAt$.value
  }

  readonly resendEligibilityWindowSeconds = 4 * 60 //can resend when there's less than 4 minutes left (1 minute has elapsed)

  readonly timeRemaining$ = this.codeExpiresAt$.pipe(
    distinctUntilChanged(),
    switchMap((codeExpiresAt) => timer(0, 1000).pipe(
      map(() => Math.max(codeExpiresAt * 1000 - Date.now(), 0)),
      distinctUntilChanged(),
      // Emit 0 once for the UI, then dispose of this expiry's interval.
      takeWhile((timeRemaining) => timeRemaining > 0, true),
    )),
  )

  get canResendCode(): boolean {
    return this.codeExpiresAt > 0 && this.resendSecondsRemaining === 0;
  }

  get resendSecondsRemaining(): number {
    if (this.codeExpiresAt <= 0) {
      return 0;
    }

    // The API accepts a resend only when strictly less than four minutes remain.
    return Math.max(
      this.codeExpiresAt - this.resendEligibilityWindowSeconds - Math.floor(Date.now() / 1000) + 1,
      0,
    );
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private configService: ConfigService,
    private logger: NGXLogger,
  ) {}

  ngOnInit(): void {
    void this.checkCanSkipSignInCode()
  }

  async checkCanSkipSignInCode(): Promise<void> {
    this.canSkipSignInCode = false
    if (this.configService.systemConfig$.apiMode !== ApiMode.Test) {
      return
    }

    this.canSkipSignInCode = await this.authService.GetSession() !== null
  }

  skipSignInCode(): void {
    void this.router.navigateByUrl('dashboard')
  }

  onCodeCompleted(code: string){
    this.loading = true

    this.logger.info("submit finish", this.currentEmail, code)
    this.authService.VaultAuthFinish(this.currentEmail, code)
      .then(async (resp) => {
        console.log("VaultAuthFinish result", resp)

        const isAuthCookieSet = await this.authService.WaitForVaultAuthSession()
        this.loading = false

        if (!isAuthCookieSet) {
          return this.router.navigateByUrl('auth/signin/cookies-required')
        }

        if(resp?.has_verified_identity && resp?.verified_identity_csp_type){
          this.logger.info("setting verified identity csp_type csp type to", resp.verified_identity_csp_type)
          this.configService.vaultProfileConfig = {
            verifiedIdentityCspType: resp.verified_identity_csp_type,
            verifiedIdentityPatientDemographics: resp.verified_identity_patient_demographics
          }
        }

        //this will attempt to redirect to Dashboard, then fallback to ID verification if not provided
        return this.router.navigateByUrl('dashboard')
      })
      .catch((err)=>{
        console.error(err)
        this.loading = false
        if(err?.name){
          this.errorMsg = "Check the code and try again, or request a new one."
        } else{
          this.errorMsg = "Something went wrong on our end. Try again, and contact support if it keeps happening."
        }
      })
  }

  onResendCode(): void {
    if (!this.canResendCode || this.resendLoading || this.loading) {
      return;
    }

    this.resendLoading = true;
    this.errorMsg = "";

    this.authService.VaultAuthResendCode(this.currentEmail)
      .then((resp) => {
        if (!resp?.expires) {
          throw new Error("Vault auth resend response did not include a code expiry");
        }
        this.codeExpiresAt = resp.expires;
        this.resendLoading = false;
      })
      .catch((err) => {
        this.logger.error("Unable to resend vault authentication code", err);
        this.resendLoading = false;
        this.errorMsg = "Unable to resend the code. Please try again.";
      });
  }

}
